import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { CircleCheckIcon, PencilIcon, TriangleAlertIcon } from "lucide-react";

import { formatDateAt } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonGroup } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Divider } from "simplydo/react/ui/divider";
import { FormField } from "simplydo/react/ui/form";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";
import { Popup } from "simplydo/react/ui/popup";
import { TextArea } from "simplydo/react/ui/textarea";

import util from "utils/utils";

import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { Sticky, TooltipButton } from "components/lib/UI";

import { useChildIdea } from "../hooks/useIdea";

type IdeaSection = NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number];
type IdeaField = IdeaSection["fields"][number];

type AssessorToolbarProps = {
  activeItemId: string | null;
  isEditing?: boolean;
};

export const AssessorToolbar = ({
  isEditing,
  activeItemId,
}: AssessorToolbarProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [idea] = useChildIdea();

  const {
    isAssessingSelectedRound,
    isAssessmentLoading,
    assessmentState,
    updateAssessment,
    submitAssessment,
    assessmentIsSubmissable,
  } = useAssessmentEditor(idea);

  const isSubmitted = assessmentState?.isSubmitted;
  const { conflictOfInterest = {} } = assessmentState || {};
  const { hasConflictOfInterest } = conflictOfInterest;
  const hasCompletedConflictDeclaration = hasConflictOfInterest !== undefined;

  const assessmentRound = useMemo(() => {
    return idea?.ownerIdeaTemplate?.assessments?.rounds?.find(
      (ar) => ar.id === (assessmentState?.assessmentRoundId || "legacy"),
    );
  }, [assessmentState?.assessmentRoundId, idea?.ownerIdeaTemplate]);

  const assessmentIsClosed =
    (assessmentState?.assessmentRoundId || "legacy") !== "legacy"
      ? assessmentRound?.assessmentClosed
      : idea?.ownerIdeaTemplate?.assessmentClosed;
  const assessmentDeadline =
    (assessmentState?.assessmentRoundId || "legacy") !== "legacy"
      ? assessmentRound?.assessmentDeadline
      : idea?.ownerIdeaTemplate?.assessmentDeadline;

  const allSections: Array<
    Omit<IdeaSection, "fields"> & {
      fields: Array<
        IdeaField & {
          isAssessment?: boolean;
          isAssessable?: boolean;
          isComplete?: boolean;
        }
      >;
    }
  > = useMemo(() => {
    return (
      idea?.ownerIdeaTemplate?.body?.map((section) => ({
        ...section,
        fields: (section.fields ?? []).map((field) => {
          if (section.type === "assessment") {
            return {
              ...field,
              isAssessment: true,
              isAssessable: util.isAssessableField(field),
              isComplete: util.assessmentFieldIsComplete(
                assessmentState?.assessment,
                field,
              ),
            };
          }
          return field;
        }),
      })) ?? []
    );
  }, [idea?.ownerIdeaTemplate?.body, assessmentState]);

  const assessmentStatus = useMemo(() => {
    let assessedFieldCount = 0;
    const assessmentSections: Array<IdeaSection> = [];

    let assessableCount = 1;
    let currentSection: IdeaSection | null = null;
    let currentField: IdeaField | null = null;

    for (let i = 0; i < allSections.length; i++) {
      const section = allSections[i];

      if (
        !section ||
        section.type !== "assessment" ||
        !section.fields ||
        section.fields.length === 0
      ) {
        continue;
      }
      if (
        (section.assessmentRoundId || "legacy") !==
        (assessmentState?.assessmentRoundId || "legacy")
      ) {
        continue;
      }

      for (let j = 0; j < section.fields.length; j++) {
        const field = section.fields[j];
        if (!field) {
          continue;
        }
        if ("isAssessment" in field && field.isAssessment) {
          if (activeItemId === field.id) {
            currentField = field;
            currentSection = section;
          }
        }
        if ("isAssessable" in field && field.isAssessable) {
          assessableCount += 1;

          if ("isComplete" in field && field.isComplete) {
            assessedFieldCount += 1;
          }
        }
      }

      assessmentSections.push(section);
    }
    return {
      currentSection,
      currentField,
      assessedFieldCount,
      assessmentFieldCount: assessableCount - 1,
      assessmentSections,
    };
  }, [allSections, activeItemId, assessmentState?.assessmentRoundId]);

  if (!isAssessingSelectedRound) {
    return null;
  }

  if (isAssessmentLoading) {
    return (
      <Sticky
        top={
          window.location.pathname === `/ideas/${idea._id}`
            ? isEditing
              ? 155
              : 110
            : 5
        }
        bottom={undefined}
        style={{
          zIndex: 75,
          marginBottom: 20,
          borderRadius: 5,
          boxShadow: "rgba(0, 0, 0, 0.3) 0px 8px 12px",
        }}
      >
        <Card fluid style={{ margin: 0 }}>
          <CardContent
            style={{
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: theme.colors.violet.solid,
              margin: 0,
            }}
          >
            <Placeholder
              style={{
                display: "flex",
                justifyContent: "space-between",
                opacity: 0.5,
              }}
            >
              <PlaceholderLine style={{ height: 30, width: 50 }} />
              <PlaceholderLine style={{ height: 30, width: 200 }} />
              <PlaceholderLine style={{ height: 30, width: 100 }} />
            </Placeholder>
          </CardContent>
        </Card>
      </Sticky>
    );
  }

  return (
    <Sticky
      top={
        window.location.pathname === `/ideas/${idea._id}`
          ? isEditing
            ? 155
            : 110
          : 5
      }
      bottom={undefined}
      style={{
        zIndex: 75,
        marginBottom: 20,
        borderRadius: 5,
        boxShadow: "rgba(0, 0, 0, 0.3) 0px 8px 12px",
      }}
    >
      <Card fluid style={{ margin: 0 }}>
        <CardContent
          style={{
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: theme.colors.violet.solid,
            borderRadius: !hasCompletedConflictDeclaration
              ? "0.3rem 0.3rem 0 0"
              : "0.3rem",
            margin: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              flex: 1,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {!hasCompletedConflictDeclaration ? null : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  flexWrap: "wrap",
                }}
              >
                {assessmentDeadline ? (
                  <div>
                    <Popup
                      trigger={
                        <Label color={assessmentIsClosed ? "yellow" : "grey"}>
                          Deadline: {formatDateAt(assessmentDeadline)}
                        </Label>
                      }
                    >
                      {
                        <p>
                          <b>Assessment deadline:</b> Assessment for this{" "}
                          {t("generic.challenge")} has a deadline of{" "}
                          <b>{formatDateAt(assessmentDeadline)}</b>. You cannot
                          edit or submit assessments after this date.
                        </p>
                      }
                    </Popup>
                  </div>
                ) : null}

                {!assessmentIsClosed &&
                assessmentState?.conflictOfInterest?.hasConflictOfInterest ===
                  false ? (
                  <Label
                    color={
                      assessmentStatus.assessedFieldCount ===
                      assessmentStatus.assessmentFieldCount
                        ? "green"
                        : "grey"
                    }
                  >
                    Assessed {assessmentStatus.assessedFieldCount} /{" "}
                    {assessmentStatus.assessmentFieldCount}
                  </Label>
                ) : null}
              </div>
            )}

            {!hasCompletedConflictDeclaration ? (
              <H3 style={{ margin: 0, padding: 0 }}>
                Assessment: Conflict of interest
              </H3>
            ) : (
              <ButtonGroup size="small">
                {assessmentIsClosed ? null : (
                  <Button
                    disabled={isSubmitted}
                    onClick={() => {
                      updateAssessment(undefined, [
                        "conflictOfInterest",
                        "hasConflictOfInterest",
                      ]);
                    }}
                    color={hasConflictOfInterest ? "red" : "green"}
                  >
                    Conflict of interest: {hasConflictOfInterest ? "Yes" : "No"}
                  </Button>
                )}
                {!assessmentIsClosed && !isSubmitted ? (
                  <Popup
                    trigger={
                      <Button
                        onClick={() => {
                          updateAssessment(undefined, [
                            "conflictOfInterest",
                            "hasConflictOfInterest",
                          ]);
                        }}
                      >
                        <Icon icon={PencilIcon} />
                      </Button>
                    }
                  >
                    Edit conflict of interest declaration
                  </Popup>
                ) : null}
              </ButtonGroup>
            )}

            {isSubmitted ? (
              <Label color="green">Assessment submitted</Label>
            ) : hasConflictOfInterest === false && !assessmentIsClosed ? (
              <TooltipButton
                size="small"
                disabled={!assessmentIsSubmissable}
                tooltip={
                  assessmentIsSubmissable
                    ? undefined
                    : "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
                }
                onClick={submitAssessment}
              >
                Submit assessment
              </TooltipButton>
            ) : null}
          </div>
        </CardContent>
        {assessmentIsClosed ? (
          <CardContent>
            <Message color="orange" size="small">
              <p>
                <b>Assessment closed:</b> Assessment is now closed for this{" "}
                {t("generic.challenge")}. You can continue to view your assigned
                ideas and previous assessments. If you think this is in error,
                please contact an administrator.
              </p>
            </Message>
          </CardContent>
        ) : !hasCompletedConflictDeclaration ? (
          <CardContent>
            <p>
              If you have a conflict of interest that would prevent you from
              objectively assessing this {t("generic.idea")} please declare it
              now. If you declare a conflict of interest you can submit the
              assessment without completing it. Once submitted, managers of this{" "}
              {t("generic.challenge")} will be notified. You can edit your
              conflict declaration at any time before submitting the assessment.
            </p>
            <p>
              You must complete the conflict of interest declaration before you
              can view the assessment.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                marginTop: 16,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Message>
                  <MessageHeader>
                    <Icon icon={CircleCheckIcon} color="green" />
                    No conflict of interest
                  </MessageHeader>

                  <Divider fitted style={{ marginTop: 5, marginBottom: 4 }} />
                  <MessageContent>
                    I confirm that I do not have any conflict of interest that
                    would prevent me from objectively assessing this{" "}
                    {t("generic.idea")}.
                    <br />
                    <Button
                      style={{ marginTop: 10 }}
                      size="small"
                      onClick={() => {
                        updateAssessment(false, [
                          "conflictOfInterest",
                          "hasConflictOfInterest",
                        ]);
                      }}
                    >{`Declare no conflict and proceed to assessment`}</Button>
                  </MessageContent>
                </Message>
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Message>
                  <MessageHeader>
                    <Icon icon={TriangleAlertIcon} color="orange" />
                    Conflict of interest
                  </MessageHeader>
                  <Divider fitted style={{ marginTop: 5, marginBottom: 4 }} />
                  <MessageContent>
                    I have a conflict of interest that would prevent me from
                    objectively assessing this {t("generic.idea")}.
                    <br />
                    <Button
                      size="small"
                      style={{ marginTop: 10 }}
                      onClick={() => {
                        updateAssessment(true, [
                          "conflictOfInterest",
                          "hasConflictOfInterest",
                        ]);
                      }}
                      disabled={isSubmitted}
                    >{`I do need to declare a conflict of interest`}</Button>
                  </MessageContent>
                </Message>
              </div>
            </div>
          </CardContent>
        ) : hasConflictOfInterest === false ? null : (
          <CardContent>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <p style={{ marginBottom: 8 }}>
                You declared a conflict of interest for this assessment, please
                provide more context below before submitting the assessment.
              </p>
              <div style={{ marginTop: 0 }}>
                <FormField disabled={isSubmitted}>
                  <TextArea
                    placeholder="Please provide any further context or information.."
                    disabled={isSubmitted}
                    value={assessmentState?.conflictOfInterest?.notes || ""}
                    onChange={(_e, { value }) =>
                      updateAssessment(value, ["conflictOfInterest", "notes"])
                    }
                  />
                </FormField>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 12,
                }}
              >
                {isSubmitted ? null : (
                  <TooltipButton
                    disabled={!assessmentIsSubmissable}
                    tooltip={
                      assessmentIsSubmissable
                        ? undefined
                        : "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
                    }
                    onClick={submitAssessment}
                    color="primary"
                  >
                    Submit assessment with conflict of interest
                  </TooltipButton>
                )}
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </Sticky>
  );
};
